import React, { useEffect, useState } from "react";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { toast } from "sonner";
import { X, Send, RotateCcw, Save, Monitor, Smartphone, Users, Calendar, BarChart3, Eye, FileText, Check } from "lucide-react";

const TABS = [
  { key: "preview",  label: "Preview",  icon: Eye },
  { key: "content",  label: "Content",  icon: FileText },
  { key: "audience", label: "Audience", icon: Users },
  { key: "schedule", label: "Schedule", icon: Calendar },
  { key: "activity", label: "Activity", icon: BarChart3 },
];

const FILTERS = [
  { key: "",              label: "All clients" },
  { key: "has_website",   label: "Has website" },
  { key: "no_website",    label: "No website" },
  { key: "low_orange_score", label: "Orange Score < 60" },
  { key: "not_using",     label: "Not using this product" },
  { key: "active_client", label: "Active clients only" },
  { key: "prospect",      label: "Prospects only" },
];

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">{label}</div>
      {children}
    </label>
  );
}
function Input(props) {
  return <input {...props} className={"w-full px-3 py-2 rounded-lg border border-[#EAEAEA] focus:outline-none focus:border-[#FF6A00] text-[14px] " + (props.className || "")}/>;
}
function TextArea(props) {
  return <textarea {...props} className={"w-full px-3 py-2 rounded-lg border border-[#EAEAEA] focus:outline-none focus:border-[#FF6A00] text-[14px] min-h-[120px] " + (props.className || "")}/>;
}

export default function CampaignStudioModal({ open, kind, tplKey, onClose, onSent }) {
  const [tab, setTab] = useState("preview");
  const [tpl, setTpl] = useState(null);
  const [audienceCounts, setAudienceCounts] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [device, setDevice] = useState("desktop");
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [audience, setAudience] = useState({ items: [], total: 0, mode: "manual" });
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [filterKey, setFilterKey] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const [activity, setActivity] = useState(null);
  const [content, setContent] = useState({
    subject: "", preheader: "", eyebrow: "", headline: "",
    body: "", cta_label: "", cta_destination: "", hero_key: "",
  });

  const loadTemplate = async () => {
    const r = await hqApi.get(`/control/studio/templates/${kind}/${tplKey}`);
    setTpl(r.data.template);
    setAudienceCounts(r.data.audience_counts);
    const t = r.data.template;
    setContent({
      subject: t.subject || "", preheader: t.preheader || "",
      eyebrow: t.eyebrow || "", headline: t.headline || "",
      body: t.body || "", cta_label: t.cta_label || "",
      cta_destination: t.cta_destination || "", hero_key: t.hero_key || "",
    });
  };

  const loadPreview = async () => {
    const r = await hqApi.get(`/control/studio/templates/${kind}/${tplKey}/preview`);
    setPreviewHtml(r.data.html); setPreviewSubject(r.data.subject);
  };

  const loadAudience = async () => {
    const r = await hqApi.get(`/control/studio/templates/${kind}/${tplKey}/eligible-audience`, {
      params: { q: q || undefined, filter_key: filterKey || undefined },
    });
    setAudience(r.data);
  };

  const loadActivity = async () => {
    const r = await hqApi.get(`/control/studio/templates/${kind}/${tplKey}/activity`);
    setActivity(r.data);
  };

  useEffect(() => {
    if (!open) return;
    setTab("preview"); setSelected(new Set()); setQ(""); setFilterKey(""); setScheduleAt("");
    loadTemplate().then(loadPreview);
  }, [open, kind, tplKey]);

  useEffect(() => {
    if (open && tab === "audience") loadAudience();
    if (open && tab === "activity") loadActivity();
  }, [tab, q, filterKey, open]);

  const save = async () => {
    setSaving(true);
    try {
      await hqApi.patch(`/control/studio/templates/${kind}/${tplKey}`, content);
      toast.success("Template saved");
      await loadTemplate(); await loadPreview();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };

  const reset = async () => {
    if (!window.confirm("Reset this template to the OrangeBySite default?")) return;
    try {
      await hqApi.post(`/control/studio/templates/${kind}/${tplKey}/reset`);
      toast.success("Reset to default");
      await loadTemplate(); await loadPreview();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reset failed"); }
  };

  const sendTest = async () => {
    if (!testTo) return toast.error("Enter a test email");
    try {
      const r = await hqApi.post(`/control/studio/templates/${kind}/${tplKey}/test`, { to_email: testTo });
      toast.success(`Test ${r.data.status} to ${r.data.to}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Test send failed"); }
  };

  const sendCampaign = async (schedule = null) => {
    const isAnnual = tpl?.audience_mode === "auto_all_eligible";
    const body = isAnnual
      ? { all_eligible: true, schedule }
      : { business_ids: Array.from(selected), schedule };
    const count = isAnnual ? audienceCounts?.eligible : selected.size;
    if (!isAnnual && selected.size === 0) return toast.error("Select at least one recipient");
    if (!window.confirm(`${schedule ? "Schedule" : "Send"} campaign to ${count} recipients?`)) return;
    try {
      const r = await hqApi.post(`/control/studio/templates/${kind}/${tplKey}/send`, body);
      if (r.data.scheduled) toast.success(`Scheduled to ${r.data.recipients} recipients`);
      else toast.success(`Sent ${r.data.sent}, skipped ${r.data.skipped}, failed ${r.data.failed}`);
      onSent && onSent(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Send failed"); }
  };

  const toggle = (id) => {
    const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s);
  };
  const selectAll = () => setSelected(new Set(audience.items.filter((i) => i.eligible).map((i) => i.business_id)));
  const clearAll = () => setSelected(new Set());

  if (!open || !tpl) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-end lg:items-center lg:justify-center p-0 lg:p-6" data-testid="hq-studio-modal">
      <div className="bg-white w-full lg:w-[95vw] lg:max-w-[1200px] lg:rounded-2xl overflow-hidden flex flex-col max-h-full">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">
              {kind === "annual" ? "Annual campaign" : kind === "product" ? "Product campaign" : "Introductory campaign"}
            </div>
            <h2 className="mt-1 font-black text-xl md:text-2xl">{tpl.label}</h2>
            <p className="text-[12px] text-[#8A8A8A] mt-1">{previewSubject || tpl.subject}</p>
          </div>
          <button onClick={onClose} data-testid="hq-studio-close" className="p-2 rounded-full hover:bg-[#F6F5F2]"><X size={18}/></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#EAEAEA] px-3 overflow-x-auto" data-testid="hq-studio-tabs">
          {TABS.map((t) => (
            <button
              key={t.key} data-testid={`hq-studio-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`px-4 py-3 text-[11px] font-black tracking-[0.16em] uppercase whitespace-nowrap border-b-2 ${
                tab === t.key ? "border-[#FF6A00] text-[#0A0A0A]" : "border-transparent text-[#8A8A8A] hover:text-[#0A0A0A]"
              }`}
            >
              <t.icon size={12} className="inline mr-1"/> {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto">
          {tab === "preview" && (
            <div className="p-5 bg-[#F6F5F2]" data-testid="hq-studio-preview">
              <div className="flex gap-2 justify-end mb-3">
                <button data-testid="hq-studio-desktop" onClick={() => setDevice("desktop")} className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.16em] uppercase ${device === "desktop" ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA]"}`}><Monitor size={12} className="inline mr-1"/>Desktop</button>
                <button data-testid="hq-studio-mobile" onClick={() => setDevice("mobile")} className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.16em] uppercase ${device === "mobile" ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA]"}`}><Smartphone size={12} className="inline mr-1"/>Mobile</button>
              </div>
              <div className="mx-auto bg-white rounded-xl shadow-lg" style={{ width: device === "mobile" ? 375 : "100%", maxWidth: 780 }}>
                <iframe title="campaign-preview" srcDoc={previewHtml} style={{ width: "100%", height: device === "mobile" ? 700 : 900, border: 0, borderRadius: 12 }} />
              </div>
            </div>
          )}

          {tab === "content" && (
            <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="hq-studio-content">
              <Field label="Subject"><Input data-testid="hq-studio-subject" value={content.subject} onChange={(e) => setContent({ ...content, subject: e.target.value })}/></Field>
              <Field label="Preheader"><Input data-testid="hq-studio-preheader" value={content.preheader} onChange={(e) => setContent({ ...content, preheader: e.target.value })}/></Field>
              <Field label="Eyebrow"><Input data-testid="hq-studio-eyebrow" value={content.eyebrow} onChange={(e) => setContent({ ...content, eyebrow: e.target.value })}/></Field>
              <Field label="Headline"><Input data-testid="hq-studio-headline" value={content.headline} onChange={(e) => setContent({ ...content, headline: e.target.value })}/></Field>
              <div className="lg:col-span-2"><Field label="Body copy"><TextArea data-testid="hq-studio-body" value={content.body} onChange={(e) => setContent({ ...content, body: e.target.value })}/></Field></div>
              <Field label="CTA label"><Input value={content.cta_label} onChange={(e) => setContent({ ...content, cta_label: e.target.value })}/></Field>
              <Field label="CTA destination path"><Input value={content.cta_destination} onChange={(e) => setContent({ ...content, cta_destination: e.target.value })}/></Field>
              <div className="lg:col-span-2 flex flex-wrap gap-2 justify-end pt-2 border-t border-[#EAEAEA]">
                <button data-testid="hq-studio-reset" onClick={reset} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase hover:border-[#FF6A00]"><RotateCcw size={12} className="inline mr-1"/> Reset to default</button>
                <button data-testid="hq-studio-save" disabled={saving} onClick={save} className="px-5 py-2 rounded-full bg-[#0A0A0A] text-white text-[10px] font-black tracking-[0.16em] uppercase disabled:opacity-50"><Save size={12} className="inline mr-1"/> Save changes</button>
              </div>
            </div>
          )}

          {tab === "audience" && (
            <div className="p-5" data-testid="hq-studio-audience">
              {tpl.audience_mode === "auto_all_eligible" ? (
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Audience</div>
                  <h3 className="mt-1 font-black text-xl">ALL ELIGIBLE CLIENTS</h3>
                  <p className="text-sm text-[#4A4A4A] mt-1">Annual campaigns are automatically sent to every eligible client. Suppressed and do-not-contact clients are always excluded.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
                    <div className="p-4 rounded-xl border border-[#EAEAEA]"><div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Total clients</div><div className="mt-2 font-black text-2xl" data-testid="hq-studio-total-clients">{audienceCounts?.total_clients ?? "—"}</div></div>
                    <div className="p-4 rounded-xl border border-[#EAEAEA]"><div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#0FA958]">Eligible</div><div className="mt-2 font-black text-2xl" data-testid="hq-studio-eligible">{audienceCounts?.eligible ?? "—"}</div></div>
                    <div className="p-4 rounded-xl border border-[#EAEAEA]"><div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Suppressed / unsubscribed</div><div className="mt-2 font-black text-2xl">{audienceCounts?.suppressed ?? "—"}</div></div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
                    <Input data-testid="hq-studio-audience-search" placeholder="Search clients…" value={q} onChange={(e) => setQ(e.target.value)}/>
                    <select data-testid="hq-studio-audience-filter" value={filterKey} onChange={(e) => setFilterKey(e.target.value)} className="px-3 py-2 rounded-lg border border-[#EAEAEA] text-[13px]">
                      {FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </select>
                    <div className="flex gap-2">
                      <button data-testid="hq-studio-select-all" onClick={selectAll} className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase">Select all eligible</button>
                      <button data-testid="hq-studio-clear" onClick={clearAll} className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase">Clear</button>
                    </div>
                  </div>
                  <div className="text-[12px] text-[#8A8A8A] mb-2">Showing {audience.items.length} of {audience.total} clients · Selected {selected.size}</div>
                  <div className="overflow-x-auto border border-[#EAEAEA] rounded-xl max-h-[400px] overflow-y-auto">
                    <table className="w-full text-[13px]">
                      <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.18em] uppercase font-bold text-[#8A8A8A] sticky top-0">
                        <tr><th className="px-3 py-2 w-8"></th><th className="text-left px-3 py-2">Business</th><th className="text-left px-3 py-2">Contact</th><th className="text-left px-3 py-2">Email</th><th className="text-left px-3 py-2">Website</th><th className="text-left px-3 py-2">Orange Score</th><th className="text-left px-3 py-2">Status</th></tr>
                      </thead>
                      <tbody>
                        {audience.items.map((i) => (
                          <tr key={i.business_id} className={`border-t border-[#EAEAEA] ${!i.eligible ? "opacity-50" : "hover:bg-[#FFF7EE]"}`}>
                            <td className="px-3 py-2"><input type="checkbox" data-testid={`hq-studio-check-${i.business_id}`} disabled={!i.eligible} checked={selected.has(i.business_id)} onChange={() => toggle(i.business_id)}/></td>
                            <td className="px-3 py-2 font-semibold">{i.business_name}</td>
                            <td className="px-3 py-2">{i.contact_name || "—"}</td>
                            <td className="px-3 py-2 text-[#4A4A4A]">{i.email || <span className="text-[#C1350B]">no email</span>}</td>
                            <td className="px-3 py-2 text-[#4A4A4A]">{i.has_website ? "✓" : "—"}</td>
                            <td className="px-3 py-2">{i.orange_score ?? "—"}</td>
                            <td className="px-3 py-2 text-[10px] tracking-[0.14em] uppercase">{i.status}</td>
                          </tr>
                        ))}
                        {audience.items.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-[#8A8A8A]">No matching clients</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "schedule" && (
            <div className="p-5 max-w-lg" data-testid="hq-studio-schedule">
              {tpl.audience_mode === "auto_all_eligible" && tpl.schedule_default && (
                <div className="mb-4 p-4 rounded-xl bg-[#FFF7EE] border border-[#FCE1C7]">
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#FF6A00]">Default schedule</div>
                  <div className="mt-1 text-sm">{tpl.schedule_default.day_rule ? tpl.schedule_default.day_rule.replace(/_/g, " ") : `${tpl.schedule_default.day || ""}/${tpl.schedule_default.month || ""}`} at {tpl.schedule_default.time || "10:00"} {tpl.schedule_default.tz || "Europe/London"}</div>
                </div>
              )}
              <div className="space-y-4">
                <Field label="Send date / time (leave blank for send-now)">
                  <Input type="datetime-local" data-testid="hq-studio-schedule-at" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}/>
                </Field>
                <div className="text-[11px] text-[#8A8A8A]">Default timezone: Europe/London</div>
              </div>
            </div>
          )}

          {tab === "activity" && (
            <div className="p-5" data-testid="hq-studio-activity">
              {!activity ? <div className="text-[#8A8A8A]">Loading…</div> : (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                    {[
                      { label: "Sent", value: activity.counts?.sent, tone: "green" },
                      { label: "Queued", value: activity.counts?.queued },
                      { label: "Skipped", value: activity.counts?.skipped },
                      { label: "Failed", value: activity.counts?.failed, tone: "red" },
                      { label: "Interested", value: activity.interested, tone: "orange" },
                    ].map((m) => (
                      <div key={m.label} className="p-3 rounded-xl border border-[#EAEAEA]">
                        <div className={`text-[10px] tracking-[0.24em] uppercase font-bold ${m.tone === "green" ? "text-[#0FA958]" : m.tone === "red" ? "text-[#C1350B]" : m.tone === "orange" ? "text-[#FF6A00]" : "text-[#8A8A8A]"}`}>{m.label}</div>
                        <div className="mt-1 font-black text-2xl">{m.value ?? 0}</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-2">Recent sends</div>
                  <div className="space-y-1 text-sm max-h-[300px] overflow-auto">
                    {(activity.recent || []).length === 0 && <div className="text-[#8A8A8A]">No sends yet.</div>}
                    {(activity.recent || []).map((c, i) => (
                      <div key={i} className="flex justify-between border-b border-[#EAEAEA] py-2">
                        <span>{c.to_email || "—"}</span>
                        <span className={`text-[10px] font-black tracking-[0.16em] uppercase ${c.status === "sent" ? "text-[#0FA958]" : c.status === "failed" ? "text-[#C1350B]" : "text-[#8A6D00]"}`}>{c.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer / action bar */}
        <div className="p-4 border-t border-[#EAEAEA] flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
          <div className="flex items-center gap-2">
            <Input data-testid="hq-studio-test-email" placeholder="test@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="max-w-[240px]"/>
            <button data-testid="hq-studio-send-test" onClick={sendTest} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase hover:border-[#FF6A00]">Send test</button>
          </div>
          <div className="flex items-center gap-2">
            {scheduleAt && (
              <button data-testid="hq-studio-schedule-btn" onClick={() => sendCampaign({ send_at: scheduleAt })} className="px-5 py-2 rounded-full bg-[#0A0A0A] text-white text-[10px] font-black tracking-[0.16em] uppercase">
                <Calendar size={12} className="inline mr-1"/> Schedule
              </button>
            )}
            <button
              data-testid="hq-studio-send-now"
              onClick={() => sendCampaign(null)}
              className="px-5 py-2 rounded-full bg-[#FF6A00] text-black text-[10px] font-black tracking-[0.16em] uppercase"
            >
              <Send size={12} className="inline mr-1"/>
              {tpl.audience_mode === "auto_all_eligible"
                ? `Send now to ${audienceCounts?.eligible ?? "?"} eligible`
                : `Send now to ${selected.size} selected`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
